// src/components/widgets/CustomWidgets.jsx
import React from "react";
import Select from "react-select";

/**
 * Generic select widget (react-select) – can be used for normal RJSF selects
 */
function CustomSelect(props) {
  const customStyles = {
    control: (provided) => ({
      ...provided,
      overflow: "visible",
      top: 0,
      padding: 8,
      borderRadius: 0,
      border: "1px solid #000",
      cursor: "pointer",
    }),
    option: (provided, state) => ({
      ...provided,
      overflow: "visible",
      top: 0,
      border: "1px solid var(--glass-color)",
      boxShadow: "none",
      color: state.isSelected ? "#fff" : "var(--text-color)",
      backgroundColor: state.isSelected
        ? "var(--leading-color)"
        : state.isFocused
        ? "color-mix(in srgb, var(--leading-color) 12%, var(--bg-color8, var(--bg-color)))"
        : "var(--bg-color8, var(--bg-color))",
      cursor: "pointer",
      padding: 8,
    }),
  };

  const {
    schema,
    required,
    value,
    options,
    placeholder,
    disabled,
    uiSchema,
    onChange,
    id,
  } = props;

  const isMulti = schema?.type === "array";
  const enumOptions = options?.enumOptions || [];

  const selectOptions = enumOptions.map((option) => ({
    ...option,
    label: option.label.replace(/_/g, " "),
  }));
  const selectedValue = isMulti
    ? selectOptions.filter(
        (option) => Array.isArray(value) && value.includes(option.value)
      )
    : selectOptions.find((option) => option.value === value) || null;

  return (
    <Select
      inputId={id}
      classNamePrefix="dataset-custom-select"
      isMulti={isMulti}
      isSearchable={false}
      styles={customStyles}
      maxMenuHeight={124}
      value={selectedValue}
      options={selectOptions}
      placeholder={placeholder}
      required={required}
      isDisabled={disabled}
      isOptionDisabled={() =>
        schema?.maxItems && Array.isArray(value)
          ? schema.maxItems <= value.length
          : false
      }
      className={
        (uiSchema?.classNames || "") +
        " react-select" +
        (disabled ? " select--is-disabled" : "")
      }
      onChange={(selected) => {
        if (isMulti) {
          onChange((selected || []).map((v) => v.value));
        } else {
          onChange(selected?.value ?? undefined);
        }
      }}
    />
  );
}

/**
 * Read-only pill display for a version field
 */
const PillDisplayWidget = (props) => {
  const { value, options = {}, schema, onChange } = props;

  const enumOptions =
    options.enumOptions ||
    (schema?.enum || []).map((v) => ({ value: v, label: v }));

  const fallbackValue =
    value ??
    schema?.default ??
    (enumOptions.length ? enumOptions[enumOptions.length - 1].value : undefined);

  React.useEffect(() => {
    if (value === undefined && fallbackValue !== undefined && onChange) {
      onChange(fallbackValue);
    }
  }, [value, fallbackValue, onChange]);

  if (!fallbackValue) {
    return null;
  }

  const idx = enumOptions.findIndex((o) => o.value === fallbackValue);
  const match = idx >= 0 ? enumOptions[idx] : null;

  let label = match ? match.label : fallbackValue;
  if (idx === enumOptions.length - 1 && enumOptions.length > 1) {
    label = `${label} (Latest)`;
  }

  return (
    <div className={options.pillClassName || "hub-pill hub-pill--readonly"}>
      {label}
    </div>
  );
};

/**
 * Version selector – looks like the pill but is interactive.
 */
const VersionSelectWidget = (props) => {
  const { value, options = {}, schema, onChange } = props;

  const enumOptions =
    options.enumOptions ||
    (schema?.enum || []).map((v) => ({ value: v, label: v }));

  const hasOptions = enumOptions && enumOptions.length > 0;
  const latestIndex = hasOptions ? enumOptions.length - 1 : -1;

  const versionOptions = hasOptions
    ? enumOptions.map((opt, idx) => ({
        ...opt,
        label:
          idx === latestIndex && enumOptions.length > 1
            ? `${opt.label} (Latest)`
            : opt.label,
      }))
    : [];

  const selected =
    hasOptions &&
    (versionOptions.find((o) => o.value === value) ||
      versionOptions[latestIndex]);

  React.useEffect(() => {
    if (value === undefined && selected && onChange) {
      onChange(selected.value);
    }
  }, [value, selected, onChange]);

  if (!hasOptions) {
    return null;
  }

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: 12,
      borderColor: "#e1e1ea",
      backgroundColor: "#f5f5f9",
      boxShadow: "none",
      paddingLeft: 4,
      "&:hover": {
        borderColor: "#c5c7d8",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 8px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#7c7f90",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontWeight: 500,
      color: "#202124",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      paddingRight: 8,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: 8,
      overflow: "hidden",
      zIndex: 20,
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: 14,
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#00b5c7"
        : state.isFocused
        ? "#e5fafc"
        : "#fff",
      color: state.isSelected ? "#fff" : "#202124",
      cursor: "pointer",
    }),
  };

  return (
    <Select
      value={selected}
      options={versionOptions}
      styles={customStyles}
      isClearable={false}
      isSearchable={false} // set true if lots of versions
      onChange={(opt) => onChange(opt.value)}
      menuPlacement="auto"
    />
  );
};

/**
 * License select – rounded input like the mock
 */
const LicenseSelectWidget = (props) => {
  const { value, options = {}, onChange, placeholder, disabled } = props;

  const enumOptions = options.enumOptions || [];

  const selected =
    enumOptions.find((o) => o.value === value) || null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "44px",
      borderRadius: 10,
      borderColor: state.isFocused ? "#00b5c7" : "#c6e5ea",
      boxShadow: state.isFocused ? "0 0 0 1px #00b5c7" : "none",
      "&:hover": {
        borderColor: "#00b5c7",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 10px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9aa3b5",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#202124",
      fontSize: 14,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      paddingRight: 10,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: 10,
      overflow: "hidden",
      zIndex: 20,
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: 14,
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#00b5c7"
        : state.isFocused
        ? "#e5fafc"
        : "#fff",
      color: state.isSelected ? "#fff" : "#202124",
      cursor: "pointer",
    }),
  };

  return (
    <Select
      value={selected}
      options={enumOptions}
      styles={customStyles}
      isClearable={false}
      isSearchable={false}
      isDisabled={disabled}
      placeholder={placeholder || "Choose a License"}
      onChange={(opt) => onChange(opt?.value)}
    />
  );
};

/* Tags input with chips for Keywords */
const TagsInputWidget = ({
  value = [],
  onChange,
  disabled,
  readonly,
  options = {},
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const tags = Array.isArray(value) ? value : [];

  const placeholder =
    options.placeholder ||
    "Enter keyword and press Enter or Click Add";
  const addButtonText = options.addButtonText || "+ Add";

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      setInputValue("");
      return;
    }
    onChange([...tags, trimmed]);
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (tag) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div className={options.wrapperClassName || "hub-tags"}>
      <div className="hub-tags__input-row">
        <input
          className="hub-tags__input"
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled || readonly}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="hub-tags__add-btn"
          disabled={disabled || readonly}
          onClick={handleAdd}
        >
          {addButtonText}
        </button>
      </div>

      {tags.length > 0 && (
        <div className="hub-tags__chips">
          {tags.map((tag) => (
            <span key={tag} className="hub-tags__chip">
              {tag}
              {!readonly && !disabled && (
                <button
                  type="button"
                  className="hub-tags__chip-close"
                  onClick={() => handleRemove(tag)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Citations widget – list of inputs with add/remove buttons
 */
const CitationsWidget = ({
  value = [],
  onChange,
  disabled,
  readonly,
  options = {},
}) => {
  const citations = Array.isArray(value) ? value : [];

  const placeholder =
    options.placeholder || "https://doi.org/… or other citation";

  const handleChangeItem = (idx, newVal) => {
    const next = [...citations];
    next[idx] = newVal;
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...citations, ""]);
  };

  const handleRemove = (idx) => {
    const next = citations.filter((_, i) => i !== idx);
    onChange(next);
  };

  const canEdit = !disabled && !readonly;

  return (
    <div className="hub-citations">
      {citations.map((cite, idx) => (
        <div key={idx} className="hub-citations__row">
          <input
            className="hub-citations__input"
            type="text"
            value={cite}
            disabled={!canEdit}
            placeholder={placeholder}
            onChange={(e) => handleChangeItem(idx, e.target.value)}
          />
          {canEdit && (
            <button
              type="button"
              className="hub-citations__remove"
              onClick={() => handleRemove(idx)}
            >
              −
            </button>
          )}
        </div>
      ))}

      {canEdit && (
        <button
          type="button"
          className="hub-citations__add-btn"
          onClick={handleAdd}
        >
          + Add Citation Field
        </button>
      )}
    </div>
  );
};

/**
 * Access card widget – shows Public / Private cards
 */
const AccessCardWidget = (props) => {
  const { value, onChange, schema, options = {} } = props;

  const enumOptions =
    options.enumOptions ||
    (schema?.enum || []).map((v) => ({ value: v, label: v }));

  const descriptions = options.descriptions || {};
  const cardClassName = options.cardClassName || "hub-access-card";

  const fallbackValue =
    value ??
    schema?.default ??
    (enumOptions.length ? enumOptions[0].value : undefined);

  React.useEffect(() => {
    if (value === undefined && fallbackValue !== undefined && onChange) {
      onChange(fallbackValue);
    }
  }, [value, fallbackValue, onChange]);

  if (!fallbackValue) return null;

  const current = fallbackValue;

  const handleClick = (val) => {
    if (val === current) return;
    onChange(val);
  };

  return (
    <div className="hub-access-cards" role="radiogroup">
      {enumOptions.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={current === opt.value}
          className={
            cardClassName +
            (current === opt.value ? ` ${cardClassName}--active` : "")
          }
          onClick={() => handleClick(opt.value)}
        >
          <div className={`${cardClassName}__dot`} />
          <div className={`${cardClassName}__content`}>
            <div className={`${cardClassName}__label`}>{opt.label}</div>
            {descriptions[opt.value] && (
              <div className={`${cardClassName}__description`}>
                {descriptions[opt.value]}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

export const CustomWidget = {
  SelectWidget: CustomSelect,     // generic select
  pillDisplay: PillDisplayWidget,
  versionSelect: VersionSelectWidget,
  licenseSelect: LicenseSelectWidget,
  tagsInput: TagsInputWidget,
  citationsField: CitationsWidget,
  accessCard: AccessCardWidget,
};
